<template>
  <v-container class="pt-3 pb-6 px-6">

    <!-- Page Header -->
    <page-header
      :title="showData ? ('Datos Caja de Sensores - ' + item.name) : ('Imagenes Caja de Sensores - ' + item.name)"
      icon="mdi-cube-outline"
      divider="-"
      :breadcrumbs="breadcrumbs.items"
      refresh
      @refresh="reload()"
    ></page-header>

    <!-- Content -->
    <v-row align="start" justify="center">
      <!-- Data Table -->
      <v-col cols="12" v-if="showData">

        <!-- Loading Bar -->
        <v-progress-linear
          indeterminate
          v-if="isLoadingData"
        ></v-progress-linear>

        <!-- Tabs -->
        <v-tabs v-model="tab" slider-color="primary" v-if="authUser">
          <v-tab :href="'#datos'">Datos</v-tab>
          <v-tab :href="'#imagenes'">Imágenes</v-tab>
          <v-tab :href="'#graficos'">Gráficos</v-tab>
          <v-spacer></v-spacer>
          <v-btn text class="ma-2" color="primary" @click="sensorsBoxList()">Volver</v-btn>
        </v-tabs>

        <v-tabs-items v-model="tab" v-show="authUser">

          <v-tab-item value="datos">
            <v-data-table
              v-show="tab === 'datos'"
              :headers="dataHeaders"
              :items="sensorsBoxData"
              class="elevation-1"
              :options.sync="paginationData"
              :server-items-length="paginationData_total"
              :footer-props="{
                'items-per-page-options': [5, 15, 30, 50]
              }"
              @update:options="getData()"
            >
              <!-- Top -->
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <!-- Date Filter -->
                  <v-menu
                    v-model="menu.data"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        label="Fecha"
                        :value="formatDate(date.data)"
                        class="shrink mr-5"
                        readonly
                        clearable
                        hide-details
                        v-on="on"
                        @click:clear="date.data = '', getData()"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="date.data" @change="menu.data = false, getData()"></v-date-picker>
                  </v-menu>
                </v-toolbar>
              </template>
              <!-- Datetime Taken -->
              <template v-slot:item.datetime_taken="{ item }">
                {{ formatDateLongES(item.datetime_taken) }}
              </template>
              <!-- Temperature -->
              <template v-slot:item.temperature="{ item }">
                {{ item.temperature && item.temperature.toFixed(2) }}
              </template>
              <!-- Relative Humedity -->
              <template v-slot:item.humidity="{ item }">
                {{ item.humidity && item.humidity.toFixed(2) }}
              </template>
              <!-- Turbine Height -->
              <template v-slot:item.wind_turbine_height="{ item }">
                {{ item.wind_turbine_height && item.wind_turbine_height.toFixed(2) }}
              </template>
              <!-- Distance to Blade Root -->
              <template v-slot:item.distance_to_blade_root="{ item }">
                {{ item.distance_to_blade_root && item.distance_to_blade_root.toFixed(2) }}
              </template>
              <!-- Wind Speed -->
              <template v-slot:item.wind_speed="{ item }">
                {{ item.wind_speed && item.wind_speed.toFixed(1) }}
              </template>
              <!-- Tilt Angle -->
              <template v-slot:item.tilt_angle="{ item }">
                {{ item.incl_x && item.incl_x.toFixed(1) }}
              </template>
              <!-- Acceleration -->
              <template v-slot:item.acceleration="{ item }">
                {{ item.accel_x && item.accel_x.toFixed(1) }}
              </template>
              <!-- No Data -->
              <template v-slot:no-data>
                No data disponible
              </template>
            </v-data-table>
          </v-tab-item>

          <v-tab-item value="imagenes">
            <v-data-table
              v-show="tab === 'imagenes'"
              :headers="imageHeaders"
              :items="sensorsBoxImages"
              class="elevation-1"
              :options.sync="paginationImages"
              :server-items-length="paginationImages_total"
              :footer-props="{
                'items-per-page-options': [5, 15, 30, 50]
              }"
              @update:options="getImages()"
            >
              <!-- Top -->
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <!-- Date Filter -->
                  <v-menu
                    v-model="menu.images"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        label="Fecha"
                        :value="formatDate(date.images)"
                        class="shrink mr-5"
                        readonly
                        clearable
                        hide-details
                        v-on="on"
                        @click:clear="date.images = '', getImages()"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="date.images" @change="menu.images = false, getImages()"></v-date-picker>
                  </v-menu>
                </v-toolbar>
              </template>
              <!-- Preview -->
              <template v-slot:item.preview="{ item }">
                <v-img
                  class="cursor-pointer my-2"
                  :src="item.download_url"
                  contain
                  aspect-ratio="1"
                  max-height="70px"
                  @click="preview(item)"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0 grey lighten-5" align="center" justify="center" >
                      <v-progress-circular indeterminate color="grey"/>
                    </v-row>
                  </template>
                </v-img>
              </template>
              <!-- Name -->
              <template v-slot:item.name="{ item }">
                {{ item.file_path.split('/')[1] }}
              </template>
              <!-- Created at -->
              <template v-slot:item.created_at="{ item }">
                {{ formatDateLongES(item.created_at) }}
              </template>
              <!-- Actions -->
              <template v-slot:item.action="{ item }">
                <a :href="item.download_url" target="_blank">
                  <v-icon class="mr-2">mdi-eye</v-icon>
                </a>
              </template>
              <!-- No Data -->
              <template v-slot:no-data>
                No hay elementos
              </template>
              <!-- No Results -->
              <template v-slot:no-results>
                No hay elementos
              </template>
            </v-data-table>
          </v-tab-item>

          <v-tab-item value="graficos">
            <sensors-box-chart />
          </v-tab-item>

        </v-tabs-items>

      </v-col>
    </v-row>

    <!-- Image Preview -->
    <image-preview
      v-model="previewDialog"
      :item="image"
      @close="previewDialog = false"
    />

  </v-container>
</template>

<script>
import PageHeader from "@/components/PageHeader";
import SensorsBoxChart from '@/components/SensorsBoxChart'
import ImagePreview from "@/components/ImagePreview";
import Util from "@/util/index";

export default {
  components: {
    PageHeader,
    SensorsBoxChart,
    ImagePreview
  },
  data: () => ({
    component: 0,
    tab: 'datos',
    authUser: false,
    showData: true,
    isLoadingData: false,
    previewDialog: false,
    menu: {
      data: false,
      images: false
    },
    date: {
      data: '',
      images: ''
    },
    image: {},
    paginationData: {
      page: 1,
      itemsPerPage: 5,
      sortBy: [],
      sortDesc: []
    },
    paginationData_total: -1,
    paginationImages: {
      page: 1,
      itemsPerPage: 5,
      sortBy: [],
      sortDesc: []
    },
    paginationImages_total: -1,
    dataHeaders: [
      { text: 'Fecha/hora', align: 'left', sortable: true, value: 'datetime_taken', width: '150px' },
      { text: 'Temperatura (°C)',  align: 'left', sortable: true, value: 'temperature', width: '200px' },
      { text: 'Humedad relativa (%)', align: 'left',  sortable: true, value: 'humidity', width: '200px' },
      { text: 'Altura respecto al suelo (m)', align: 'left', sortable: true, value: 'wind_turbine_height', width: '220px' },
      { text: 'Radio de la pala (m)', align: 'left', sortable: true, value: 'distance_to_blade_root', width: '200px' },
      { text: 'Velocidad del viento (m/s)', align: 'left', sortable: true, value: 'wind_speed', width: '220px' },
      { text: 'Inclinación (grados)', align: 'left', sortable: true, value: 'tilt_angle', width: '200px' },
      { text: 'Aceleración (m/s2)', align: 'left', sortable: true, value: 'acceleration', width: '200px' },
      { text: 'Posición GPS', align: 'left', sortable: true, value: 'gps_position', width: '200px' }
    ],
    imageHeaders: [
      { text: '', align: 'center', sortable: true, value: 'preview' },
      { text: 'Fecha/hora', align: 'center', sortable: true, value: 'datetime_taken' },
      { text: 'Nombre imagen', align: 'center', sortable: true, value: 'name' },
      { text: 'Acción', align: 'center',  sortable: false, value: 'action' }
    ],
    breadcrumbs: {
      items: [
        { text: "Configuración",  disabled: true },
        { text: "Cajas de Sensores", disabled: true }
      ]
    }
  }),
  computed: {
    item () {
      return this.$store.state.sensors_boxes.item
    },
    sensorsBoxData () {
      return this.$store.state.sensors_boxes.data
    },
    sensorsBoxImages () {
      return this.$store.state.sensors_boxes.images
    },
  },
  methods: {
    preview (item) {
      this.image = item
      this.previewDialog = true
    },
    formatDate(date) {
      return Util.formatDate(date)
    },
    formatDateLongES(date) {
      return Util.formatDateLongES(date)
    },
    sensorsBoxList () {
      this.$router.push({ name: "sensors-list" });
    },
    reload () {
      this.getData()
      this.getImages()
    },
    async getData() {
      this.isLoadingData = true
      const body = {
        ...this.paginationData,
        id: this.$route.params.id,
        date: this.date.data
      }
      try {
        const res = await this.$store.dispatch('sensors_boxes/getData', body)
        if (res) {
          const meta = res.data.meta
          this.paginationData_total = meta.total
        }
      }
      catch (error) {
        if (error.response.status === 403 ) {
          this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
            color: "error",
            x: "center",
            y: "top",
            icon: "mdi-information",
            timeout: 0,
            showClose: true,
            closeText: "Cerrar"
          });
          this.$router.push({ name: 'home'})
        } else {
          var errorToastMessage = "Ha ocurrido un error cargando los datos de la caja de sensores."
          Util.showErrorToast(this, error, errorToastMessage);
        }
      }
      finally {
        this.isLoadingData = false
      }
    },
    async getImages() {
      this.isLoadingData = true
      const body = {
        ...this.paginationImages,
        id: this.$route.params.id,
        date: this.date.images
      }
      try {
        const res = await this.$store.dispatch('sensors_boxes/getImages', body)
        if (res) {
          const meta = res.data.meta
          this.paginationImages_total = meta.total
        }
      }
      catch (error) {
        if (error.response.status === 403 ) {
          this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
            color: "error",
            x: "center",
            y: "top",
            icon: "mdi-information",
            timeout: 0,
            showClose: true,
            closeText: "Cerrar"
          });
          this.$router.push({ name: 'home'})
        } else {
          var errorToastMessage = "Ha ocurrido un error cargando las imágenes de la caja de sensores."
          Util.showErrorToast(this, error, errorToastMessage);
        }
      }
      finally {
        this.isLoadingData = false
      }
    },
    async init () {
      this.isLoadingData = true
      try {
        await this.$store.dispatch('sensors_boxes/get', this.$route.params.id)
        this.authUser = true
      }
      catch (error) {
        if (error.response.status === 403 ) {
          this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
            color: "error",
            x: "center",
            y: "top",
            icon: "mdi-information",
            timeout: 0,
            showClose: true,
            closeText: "Cerrar"
          });
          this.$router.push({ name: 'home'})
        } else {
          var errorToastMessage = "Ha ocurrido un error cargando los datos de las cajas de sensores."
          Util.showErrorToast(this, error, errorToastMessage);
        }
      }
      finally {
        this.isLoadingData = false
      }
    }
  },
  created () {
    this.init()
  }
}
</script>
